import { useState } from 'react';
import Chevron from 'components/Chevron';
import PrizeTierBadgeAndTitle, {
  Size,
} from 'components/prizes/PrizeTierDropdownSelector/PrizeTierBadgeAndTitle';
import React from 'react';
import { PrizeTierProps } from 'constants/PrizeTiers';

export const roughValueComponent = (
  solidPoundSignString: string,
  fadedPoundSignString: string
) => (
  <p>
    {solidPoundSignString}
    <span style={{ opacity: 0.5 }}>{fadedPoundSignString}</span>
  </p>
);

export interface IPrizeTierDropdownSelectorProps {
  initialValue?: string | null;
  options: { code: string; props: PrizeTierProps }[];
  onChange: (newValue: string | null) => void;
  style?: React.CSSProperties;
}

const PrizeTierDropdownSelector = ({
  initialValue,
  options,
  onChange,
  style = undefined,
}: IPrizeTierDropdownSelectorProps) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    initialValue && options.map(({ code }) => code).includes(initialValue)
      ? initialValue
      : null
  );

  const optionsToRender = [
    {
      code: null,
      titleComponent: (
        <p
          style={{
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '1.1rem',
            margin: 0,
          }}
        >
          View All
        </p>
      ),
      roughValueComponent: roughValueComponent('£ - £££££', ''),
    },
    ...options.map(({ code, props: { title, subtitle, badge, poundSigns } }) => {
      return {
        code,
        titleComponent: (
          <PrizeTierBadgeAndTitle
            title={title}
            subtitle={subtitle}
            badge={badge}
            size={Size.COMPACT}
          />
        ),
        roughValueComponent: roughValueComponent(
          poundSigns.solid,
          poundSigns.faded
        ),
      };
    }),
  ];

  // First row is always shown
  const renderFirstRowLeftSide = () => {
    if (expanded || !selectedValue)
      return (
        <p
          style={{
            margin: 0,
            fontSize: '1.1rem',
            color: '#9B9B9B',
          }}
        >
          Filter by prize tier
        </p>
      );

    const { title, subtitle, badge } = options.find(
      ({ code }) => code === selectedValue
    )!.props;

    return (
      <PrizeTierBadgeAndTitle
        title={title}
        subtitle={subtitle}
        badge={badge}
        size={Size.COMPACT}
      />
    );
  };

  return (
    <div
      className={`prizeTierDropdownSelectorContainer p-0 ${
        expanded ? '!border-white' : ''
      }`}
      style={style}
    >
      <div
        className={`firstRow p-6 ${
          expanded ? 'p-6 rounded-t-[25px]' : ' rounded-[30px]'
        }`}
        style={
          expanded
            ? {
                height: '40px',
                boxShadow: '0px 3px 100px 0px rgba(0, 0, 0, 0.4)',
              }
            : {}
        }
        onClick={() => setExpanded(!expanded)}
      >
        {renderFirstRowLeftSide()}

        <Chevron
          direction="down"
          reversed={expanded}
          style={{ color: '#AAA' }}
        />
      </div>

      {expanded && (
        <div
          className="dropdownMenu p-3 rounded-b-[30px] hover:cursor-pointer"
          style={{
            width: '100%',
            background: 'white',
            position: 'absolute',
            zIndex: 1,
            boxShadow: expanded
              ? '0px 50px 50px -1px rgba(0, 0, 0, 0.4)'
              : 'none',
          }}
        >
          {optionsToRender.map((option, index) => {
            return (
              <div
                key={option.code ?? 'null'}
                style={{
                  borderColor: '#E1E1E1',
                }}
                className={`${
                  index !== optionsToRender.length - 1
                    ? 'border border-solid border-2 border-t-0 border-x-0'
                    : ''
                } p-2`}
              >
                <div
                  style={
                    selectedValue && option.code === selectedValue
                      ? { background: '#F6F6F6' }
                      : {}
                  }
                  className={'px-4 hoverColor !rounded-lg dropdownRow'}
                  onClick={() => {
                    setSelectedValue(option.code);
                    onChange(option.code);
                    setExpanded(false);
                  }}
                >
                  {option.titleComponent}

                  {option.roughValueComponent}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style jsx>
        {`
          .prizeTierDropdownSelectorContainer {
            border: 2px solid #e1e1e1;
            border-radius: 35px;
            width: 100%;
            isolation: isolate;
            z-index: 1;
            font-family: Montserrat-SemiBold, Montserrat, sans-serif;
            position: relative;
          }

          .hoverColor:hover {
            background-color: #f6f6f6;
          }

          .firstRow {
            height: 40px;
          }

          .firstRow,
          .dropdownRow {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .dropdownRow {
            transition: 0.2s;
            min-height: 40px;
          }
        `}
      </style>
    </div>
  );
};

export default PrizeTierDropdownSelector;
